// src/pages/RegisterPage.tsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import GlassModal from '../components/GlassModal';
import PrivacyPolicy from '../components/PrivacyPolicy';

const css = `
.lm-login {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #dbeafe 0%, #e9d5ff 45%, #fce7f3 100%);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  padding: 120px 20px 40px;
}
.lm-clouds { position: absolute; inset: 0; pointer-events: none; }
.lm-cloud {
  position: absolute;
  background: #fff;
  border-radius: 100px;
  opacity: 0.65;
  filter: blur(2px);
}
.lm-cloud::before, .lm-cloud::after {
  content: ''; position: absolute; background: #fff; border-radius: 50%;
}
.lm-cloud-1 { width: 180px; height: 50px; top: 12%; left: -220px; animation: lm-drift 38s linear infinite; }
.lm-cloud-1::before { width: 90px; height: 90px; top: -40px; left: 30px; }
.lm-cloud-1::after  { width: 70px; height: 70px; top: -28px; left: 90px; }
.lm-cloud-2 { width: 140px; height: 40px; top: 28%; left: -180px; animation: lm-drift 52s linear infinite; animation-delay: -10s; }
.lm-cloud-2::before { width: 70px; height: 70px; top: -32px; left: 22px; }
.lm-cloud-2::after  { width: 55px; height: 55px; top: -22px; left: 70px; }
.lm-cloud-3 { width: 220px; height: 60px; top: 68%; left: -260px; animation: lm-drift 64s linear infinite; animation-delay: -25s; opacity: 0.5; }
.lm-cloud-3::before { width: 110px; height: 110px; top: -50px; left: 40px; }
.lm-cloud-3::after  { width: 85px; height: 85px; top: -34px; left: 110px; }
.lm-cloud-4 { width: 120px; height: 36px; top: 82%; left: -160px; animation: lm-drift 45s linear infinite; animation-delay: -15s; opacity: 0.45; }
.lm-cloud-4::before { width: 60px; height: 60px; top: -26px; left: 18px; }
.lm-cloud-4::after  { width: 50px; height: 50px; top: -20px; left: 60px; }
@keyframes lm-drift {
  from { transform: translateX(0); }
  to   { transform: translateX(calc(100vw + 300px)); }
}
.lm-glow {
  position: absolute;
  top: -180px; right: -180px;
  width: 520px; height: 520px;
  background: radial-gradient(circle, rgba(255,237,213,0.9) 0%, rgba(255,237,213,0) 65%);
  pointer-events: none;
  animation: lm-pulse 8s ease-in-out infinite;
}
@keyframes lm-pulse {
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50%      { transform: scale(1.08); opacity: 1; }
}
.lm-back {
  position: absolute;
  top: 22px; left: 22px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 16px;
  border: 1px solid rgba(255,255,255,0.7);
  border-radius: 100px;
  background: rgba(255,255,255,0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #4c1d95;
  font-size: 13px; font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.15s, background 0.2s, box-shadow 0.2s;
  z-index: 10;
}
.lm-back:hover {
  background: #fff;
  transform: translateX(-3px);
  box-shadow: 0 6px 16px rgba(76,29,149,0.15);
}
.lm-card-wrap {
  position: relative;
  width: 100%;
  max-width: 420px;
  z-index: 2;
}
.lm-llama {
  position: absolute;
  top: -85px;
  left: 50%;
  width: 130px;
  height: auto;
  transform: translateX(-50%);
  z-index: 3;
  pointer-events: none;
  filter: drop-shadow(0 18px 28px rgba(76, 29, 149, 0.28));
  animation: lm-llama-bob 4s ease-in-out infinite;
  user-select: none;
}
@keyframes lm-llama-bob {
  0%, 100% { transform: translateX(-50%) translateY(0) rotate(-2deg); }
  50%      { transform: translateX(-50%) translateY(-10px) rotate(2deg); }
}
.lm-llama-shadow {
  position: absolute;
  top: -8px; left: 50%;
  width: 95px; height: 14px;
  transform: translateX(-50%);
  background: radial-gradient(ellipse, rgba(76,29,149,0.25) 0%, rgba(76,29,149,0) 70%);
  filter: blur(5px);
  z-index: 1;
  animation: lm-shadow-scale 4s ease-in-out infinite;
}
@keyframes lm-shadow-scale {
  0%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.9; }
  50%      { transform: translateX(-50%) scaleX(0.82); opacity: 0.55; }
}
.lm-card {
  position: relative;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 28px;
  padding: 44px 36px 32px;
  box-shadow:
    0 30px 60px -20px rgba(76, 29, 149, 0.25),
    0 12px 24px -12px rgba(76, 29, 149, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  animation: lm-card-in 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
@keyframes lm-card-in {
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.lm-logo {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-bottom: 20px;
  font-weight: 700; font-size: 18px;
  color: #4c1d95;
  letter-spacing: -0.3px;
}
.lm-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #1e1b4b;
  text-align: center;
  letter-spacing: -0.5px;
}
.lm-subtitle {
  margin: 0 0 28px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}
.lm-form { display: flex; flex-direction: column; gap: 18px; }
.lm-field { display: flex; flex-direction: column; gap: 6px; }
.lm-label { font-size: 13px; font-weight: 600; color: #4b5563; padding-left: 4px; }
.lm-input-wrap {
  position: relative;
  display: flex; align-items: center;
  background: rgba(255, 255, 255, 0.7);
  border: 1.5px solid rgba(167, 139, 250, 0.35);
  border-radius: 14px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.lm-input-wrap:focus-within {
  border-color: #8b5cf6;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.15);
}
.lm-input-wrap input {
  flex: 1;
  border: 0; outline: 0; background: transparent;
  padding: 13px 16px;
  font-size: 15px;
  color: #1e1b4b;
  font-family: inherit;
  min-width: 0;
}
.lm-input-wrap input::placeholder { color: #9ca3af; }
.lm-eye {
  border: 0; background: transparent; cursor: pointer;
  padding: 0 12px;
  color: #7c3aed;
  opacity: 0.6;
  display: flex; align-items: center;
  transition: opacity 0.2s, transform 0.15s;
}
.lm-eye:hover { opacity: 1; transform: scale(1.1); }
.lm-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  margin-top: -4px;
}
.lm-check {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #4b5563;
  cursor: pointer;
  user-select: none;
}
.lm-check input {
  width: 16px; height: 16px;
  accent-color: #8b5cf6;
  cursor: pointer;
}
.lm-forgot-link {
  border: 0; background: transparent;
  color: #7c3aed;
  font-size: 13px; font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.2s;
}
.lm-forgot-link:hover { color: #4c1d95; text-decoration: underline; }
.lm-error {
  display: flex; align-items: center; gap: 8px;
  background: rgba(254, 226, 226, 0.8);
  border: 1px solid rgba(248, 113, 113, 0.4);
  color: #b91c1c;
  font-size: 13px;
  padding: 10px 14px;
  border-radius: 10px;
  animation: lm-shake 0.4s;
}
@keyframes lm-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-6px); }
  75% { transform: translateX(6px); }
}
.lm-submit {
  margin-top: 6px;
  padding: 14px 20px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 12px 24px -10px rgba(139, 92, 246, 0.7);
  position: relative;
  overflow: hidden;
  width: 100%;
}
.lm-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px -10px rgba(139, 92, 246, 0.85);
}
.lm-submit:active:not(:disabled) { transform: translateY(0); }
.lm-submit:disabled { opacity: 0.7; cursor: not-allowed; }
.lm-arrow { transition: transform 0.2s; }
.lm-submit:hover .lm-arrow { transform: translateX(4px); }
.lm-spinner::after {
  content: '';
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  margin-left: 10px;
  vertical-align: -2px;
  animation: lm-spin 0.7s linear infinite;
}
@keyframes lm-spin { to { transform: rotate(360deg); } }
.lm-links {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 22px;
  font-size: 14px;
  color: #6b7280;
}
.lm-register {
  border: 0; background: transparent;
  color: #7c3aed;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 0;
  transition: color 0.2s;
}
.lm-register:hover { color: #4c1d95; text-decoration: underline; }
.lm-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(30, 27, 75, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  z-index: 100;
  animation: lm-fade-in 0.2s;
}
@keyframes lm-fade-in { from { opacity: 0; } to { opacity: 1; } }
.lm-modal {
  position: relative;
  background: #fff;
  border-radius: 24px;
  padding: 36px 32px 28px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 30px 80px -20px rgba(76, 29, 149, 0.4);
  animation: lm-modal-in 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
@keyframes lm-modal-in {
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.lm-modal-close {
  position: absolute; top: 14px; right: 14px;
  border: 0; background: rgba(139, 92, 246, 0.08);
  color: #7c3aed;
  width: 32px; height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.lm-modal-close:hover { background: rgba(139, 92, 246, 0.18); }
.lm-modal-icon {
  display: flex; align-items: center; justify-content: center;
  width: 56px; height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ede9fe, #fae8ff);
  color: #7c3aed;
  margin: 0 auto 16px;
}
.lm-modal-title {
  margin: 0 0 6px;
  font-size: 20px; font-weight: 700;
  color: #1e1b4b;
  text-align: center;
}
.lm-modal-sub {
  margin: 0 0 22px;
  font-size: 14px; color: #6b7280;
  text-align: center;
  line-height: 1.5;
}
.lm-success {
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: 12px;
}
.lm-success-icon {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
  display: flex; align-items: center; justify-content: center;
}
.lm-success p {
  margin: 0 0 8px;
  font-size: 14px; color: #4b5563;
  line-height: 1.5;
}
@media (max-width: 480px) {
  .lm-login { padding: 100px 16px 24px; }
  .lm-card { padding: 36px 22px 26px; border-radius: 22px; }
  .lm-llama { width: 150px; top: -100px; }
  .lm-llama-shadow { width: 110px; }
  .lm-title { font-size: 22px; }
  .lm-modal { padding: 32px 24px 24px; border-radius: 20px; }
  .lm-back { top: 14px; left: 14px; font-size: 12px; padding: 7px 12px; }
}
`;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [company, setCompany] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.trim()) { setModalMessage('Введите название компании'); return; }
    if (!name.trim()) { setModalMessage('Введите ваше имя'); return; }
    if (!agreed) { setModalMessage('Необходимо дать согласие на обработку персональных данных'); return; }
    if (password !== confirmPassword) { setModalMessage('Пароли не совпадают'); return; }
    if (password.length < 6) { setModalMessage('Пароль должен содержать минимум 6 символов'); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, company })
      });
      const data = await res.json();
      if (!res.ok) { setModalMessage(data.error || 'Ошибка при регистрации'); return; }
      if (data.requiresVerification) {
        navigate(`/verify-email?email=${encodeURIComponent(data.email || email)}`);
        return;
      }
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
        return;
      }
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setModalMessage('Ошибка сети. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lm-login">
      <style>{css}</style>

      <div className="lm-clouds" aria-hidden="true">
        <div className="lm-cloud lm-cloud-1" />
        <div className="lm-cloud lm-cloud-2" />
        <div className="lm-cloud lm-cloud-3" />
        <div className="lm-cloud lm-cloud-4" />
      </div>

      <div className="lm-glow" aria-hidden="true" />

      <button onClick={() => navigate('/')} className="lm-back" aria-label="На главную">
        <ArrowLeft size={16} /> На главную
      </button>

      <div className="lm-card-wrap">
        <img
          src="/images/10.png"
          alt=""
          className="lm-llama"
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="lm-llama-shadow" aria-hidden="true" />

        <div className="lm-card">
          <h1 className="lm-title">Добро пожаловать!</h1>
          <p className="lm-subtitle">Создайте аккаунт, чтобы начать работу</p>

          <form onSubmit={handleSubmit} className="lm-form" noValidate>
            <label className="lm-field">
              <div className="lm-input-wrap">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ваше имя"
                  className="lm-input"
                  required
                />
              </div>
            </label>

            <label className="lm-field">
              <div className="lm-input-wrap">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (you@company.ru)"
                  className="lm-input"
                  required
                />
              </div>
            </label>

            <label className="lm-field">
              <div className="lm-input-wrap">
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Название компании"
                  className="lm-input"
                  required
                />
              </div>
            </label>

            <label className="lm-field">
              <div className="lm-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Пароль (минимум 6 символов)"
                  className="lm-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="lm-eye"
                  aria-label="Показать пароль"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <label className="lm-field">
              <div className="lm-input-wrap">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите пароль"
                  className="lm-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="lm-eye"
                  aria-label="Показать пароль"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px', marginBottom: '24px', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Я согласен на обработку персональных данных
              </label>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }}
                style={{ fontSize: '13px', color: '#6a11cb', textDecoration: 'underline', fontWeight: 500 }}
              >
                Политика конфиденциальности
              </a>
            </div>

            <button type="submit" className="lm-submit" disabled={loading}>
              {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </button>

            <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#64748b' }}>
              Уже есть аккаунт? <Link to="/login" style={{ color: '#6a11cb', fontWeight: 600, textDecoration: 'none' }}>Войти</Link>
            </p>
          </form>
        </div>
      </div>

      {modalMessage && <GlassModal message={modalMessage} onClose={() => setModalMessage(null)} />}
      <PrivacyPolicy isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
};

export default RegisterPage;
